// UploadCenter.tsx

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box as MuiBox,
    Paper as MuiPaper,
    Typography as MuiTypography,
    Button as MuiButton
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { UploadCloud } from "lucide-react";
import { useAuth } from "@clerk/clerk-react";

import { T } from "@/components/FileViewer/filesV6";
import FileViewer, { FileViewerRef } from "@/components/FileViewer/FileViewer";
import FileUploadVectorFileModal from "@/components/Toolkit/FileUploadVectorFileModal";
import FileScopeModal, { SavedScopeData } from "@/components/FileViewer/FileScopeModal";
import { VectorFile } from "@/api/models/vector_file";
import fetchWithAuth from "@/api/secureFetch";
import { markWorkProductsNeedingRebuild } from "@/api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { useProgrammaticIntakeQueue } from "./useProgrammaticIntakeQueue";
import { NavigationBackButton } from "./NavigationBackButton";

const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

interface FileScope {
    global: { company_id: number; company_name: string } | null;
    projects: Array<{ project_id: number; project_name: string }>;
    features: Array<{ feature_id: number; feature_name: string }>;
}

interface UploadCenterProps {
    hideHeader?: boolean;
    /** Files handed to this page programmatically (FDE chat drops →
     *  send_file_to_uploads). Fed one at a time through the same upload modal
     *  as a manual drop/pick. */
    intakeFiles?: File[];
    onIntakeConsumed?: () => void;
    /** Fires after the user saves a scope assignment for an uploaded file —
     *  the host's hook point for the return-to-origin offer. */
    onAssignmentComplete?: () => void;
    /** Changes when an external action (such as FDE) updates file assignments. */
    refreshSignal?: number;
    /** Shown only when this page was opened from the product's Context Mesh. */
    onBackToContextMesh?: () => void;
}

export const UploadCenter: React.FC<UploadCenterProps> = ({
    hideHeader = false,
    intakeFiles,
    onIntakeConsumed,
    onAssignmentComplete,
    refreshSignal,
    onBackToContextMesh
}) => {
    const { getToken } = useAuth();
    const fileViewerRef = useRef<FileViewerRef>(null);

    const [fileOpen, setFileOpen] = useState(false);
    const [fileToUpload, setFileToUpload] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showScopeModal, setShowScopeModal] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<VectorFile | null>(null);
    const [uploadedFileScope, setUploadedFileScope] = useState<FileScope | null>(null);
    const [pendingScopeAssignments, setPendingScopeAssignments] = useState<Array<{ file: VectorFile; scope: FileScope | null }>>([]);
    const [completedAssignments, setCompletedAssignments] = useState(0);
    const returnNotifiedForBatchRef = useRef(false);
    const [fileTypeError, setFileTypeError] = useState<string | null>(null);
    const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);

    // NEW: ref for a dedicated manual file input (button-driven)
    const manualInputRef = useRef<HTMLInputElement | null>(null);
    const lastRefreshSignalRef = useRef(refreshSignal);

    useEffect(() => {
        if (refreshSignal === undefined || refreshSignal === lastRefreshSignalRef.current) return;
        lastRefreshSignalRef.current = refreshSignal;
        fileViewerRef.current?.refresh();
    }, [refreshSignal]);

    // Fetch existing scope for a file (used after upload to preserve existing project associations)
    const fetchFileScope = useCallback(async (fileId: number): Promise<FileScope | null> => {
        try {
            const token = await getToken();
            if (!token) {
                console.error("No auth token available");
                return null;
            }

            const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/get_file_scopes`, token, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ file_ids: [fileId] })
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch file scopes: ${response.statusText}`);
            }

            const data = await response.json();
            const scopes = data.scopes || {};
            return scopes[fileId] || null;
        } catch (e) {
            console.error("Error fetching file scope:", e);
            return null;
        }
    }, [getToken]);

    const allowedExtensions = [
        ".pdf", ".doc", ".docx", ".odt", ".rtf",
        ".xls", ".xlsx", ".ods",
        ".ppt", ".pptx", ".odp",
        ".txt", ".html", ".htm", ".css", ".csv", ".tsv",
        ".js", ".mjs", ".ts", ".tsx", ".jsx",
        ".py", ".java", ".c", ".h", ".cs", ".php", ".rb", ".pl",
        ".sh", ".bash",
        ".xml", ".yaml", ".yml", ".md", ".markdown",
        ".sql", ".json", ".graphql", ".gql",
        ".xhtml", ".vue", ".svelte",
        ".ini", ".cfg", ".conf", ".env", ".toml", ".properties",
        ".log", ".rst", ".tex"
    ];

    const isFileAllowed = useCallback((file: File): boolean => {
        const fileName = file.name.toLowerCase();
        return allowedExtensions.some(ext => fileName.endsWith(ext));
    }, []);

    const enqueueFilesForUpload = useCallback((files: File[]) => {
        if (!files.length) return;
        const allowed: File[] = [];
        let sawRejectedType = false;
        files.forEach(file => {
            if (isFileAllowed(file)) allowed.push(file);
            else sawRejectedType = true;
        });
        if (sawRejectedType) {
            setFileTypeError(`File type not supported.\nAllowed types: ${allowedExtensions.join(", ")}`);
        }
        if (allowed.length > 0) {
            setPendingUploadFiles(prev => [...prev, ...allowed]);
        }
    }, [isFileAllowed]);

    // Drag & drop / picker queue → feed one file at a time into the upload modal.
    const onDrop = useCallback((accepted: File[]) => {
        enqueueFilesForUpload(accepted);
    }, [enqueueFilesForUpload]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple: true,
        noClick: true,
        noKeyboard: true,
        accept: {
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "application/vnd.oasis.opendocument.text": [".odt"],
            "application/rtf": [".rtf"],
            "application/vnd.ms-excel": [".xls"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
            "application/vnd.oasis.opendocument.spreadsheet": [".ods"],
            "application/vnd.ms-powerpoint": [".ppt"],
            "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
            "application/vnd.oasis.opendocument.presentation": [".odp"],
            "text/plain": [".txt", ".log", ".ini", ".cfg", ".conf", ".env", ".properties"],
            "text/html": [".html", ".htm", ".xhtml"],
            "text/css": [".css"],
            "text/csv": [".csv", ".tsv"],
            "text/javascript": [".js", ".mjs"],
            "text/typescript": [".ts", ".tsx"],
            "text/jsx": [".jsx"],
            "text/x-python": [".py"],
            "text/x-java": [".java"],
            "text/x-c": [".c", ".h"],
            "text/x-csharp": [".cs"],
            "text/x-php": [".php"],
            "text/x-ruby": [".rb"],
            "text/x-perl": [".pl"],
            "text/x-shellscript": [".sh", ".bash"],
            "text/xml": [".xml"],
            "text/yaml": [".yaml", ".yml"],
            "text/markdown": [".md", ".markdown"],
            "application/sql": [".sql"],
            "application/json": [".json"],
            "application/graphql": [".graphql", ".gql"],
            "text/x-vue": [".vue"],
            "text/x-svelte": [".svelte"],
            "application/toml": [".toml"],
            "text/x-rst": [".rst"],
            "text/x-tex": [".tex"]
        }
    });

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => setIsDragOver(false), []);

    const handleDropCard = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = e.dataTransfer?.files;
        if (files && files.length > 0) {
            enqueueFilesForUpload(Array.from(files));
        }
    }, [enqueueFilesForUpload]);

    const handleBrowseClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation(); // don't bubble to the dropzone root
        manualInputRef.current?.click();
    }, []);

    // Chat-routed intake: enqueue the next pending file and let the shared
    // queue effect feed it into the upload modal in order.
    useProgrammaticIntakeQueue({
        intakeFiles,
        onIntakeConsumed,
        onConsumeFile: file => enqueueFilesForUpload([file])
    });

    useEffect(() => {
        if (fileOpen || showScopeModal || fileToUpload || pendingUploadFiles.length === 0) return;
        const next = pendingUploadFiles[0];
        setPendingUploadFiles(prev => (prev[0] === next ? prev.slice(1) : prev));
        setFileToUpload(next);
        setFileOpen(true);
    }, [fileOpen, showScopeModal, fileToUpload, pendingUploadFiles]);

    // Process one scope assignment at a time to avoid overwriting the current
    // modal state when multiple uploads complete close together.
    useEffect(() => {
        if (showScopeModal || uploadedFile || pendingScopeAssignments.length === 0) return;
        const next = pendingScopeAssignments[0];
        setPendingScopeAssignments(prev => (prev[0] === next ? prev.slice(1) : prev));
        setUploadedFile(next.file);
        setUploadedFileScope(next.scope);
        setShowScopeModal(true);
    }, [showScopeModal, uploadedFile, pendingScopeAssignments]);

    // Notify return-to-origin only once the entire upload+scope pipeline is
    // drained. Emitting per-file can navigate away mid-batch and strand
    // pending files/scopes.
    useEffect(() => {
        const pipelineIdle =
            !fileOpen &&
            !showScopeModal &&
            !fileToUpload &&
            !uploadedFile &&
            pendingUploadFiles.length === 0 &&
            pendingScopeAssignments.length === 0;
        if (!pipelineIdle) return;
        if (completedAssignments === 0) return;
        if (returnNotifiedForBatchRef.current) return;
        returnNotifiedForBatchRef.current = true;
        onAssignmentComplete?.();
        setCompletedAssignments(0);
    }, [
        completedAssignments,
        fileOpen,
        fileToUpload,
        onAssignmentComplete,
        pendingScopeAssignments.length,
        pendingUploadFiles.length,
        showScopeModal,
        uploadedFile
    ]);

    const handleManualInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = e.target.files ? Array.from(e.target.files) : [];
        enqueueFilesForUpload(picked);
        // allow selecting the same file again later
        e.currentTarget.value = "";
    }, [enqueueFilesForUpload]);

    return (
        <MuiBox sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
            {!hideHeader && (
                <MuiPaper
                    elevation={0}
                    sx={{
                        height: 64,
                        minHeight: 64,
                        maxHeight: 64,
                        display: "flex",
                        alignItems: "center",
                        px: 3,
                        borderBottom: 1,
                        borderColor: "divider",
                        borderRadius: 0,
                        bgcolor: "#F1F5FE",
                        flexShrink: 0
                    }}
                >
                    {onBackToContextMesh && (
                        <NavigationBackButton
                            label="Back to Context Mesh"
                            onClick={onBackToContextMesh}
                            description="Return to the product Context Mesh"
                            bridgeId="btn.back-to-context-mesh"
                            testId="back-to-context-mesh"
                        />
                    )}
                    <MuiTypography variant="h4" sx={{ fontWeight: 600, color: "text.primary", fontSize: "1.25rem" }}>
                        Files & Attachments
                    </MuiTypography>
                    <MuiTypography variant="h4" sx={{ color: "text.disabled", fontWeight: 300, fontSize: "1.25rem", mx: 2 }}>
                        |
                    </MuiTypography>
                    <MuiTypography variant="body1" sx={{ color: "text.secondary", fontSize: "0.875rem", fontWeight: 400 }}>
                        One flat list. Every file shows the products it feeds — assign to one, several, or all.
                    </MuiTypography>
                </MuiPaper>
            )}

            <MuiBox sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {/* Compact uploader strip (V6) */}
                <MuiBox
                    {...getRootProps()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropCard}
                    onClick={handleBrowseClick}
                    sx={{
                        mx: 3, mt: 2, px: "14px", py: "10px",
                        display: "flex", alignItems: "center", gap: "12px", flexShrink: 0,
                        border: `2px dashed ${isDragOver || isDragActive ? T.primary : "rgba(0,0,0,0.12)"}`,
                        borderRadius: "11px",
                        backgroundColor: isDragOver || isDragActive ? T.spineSoft : "transparent",
                        transition: "all 0.2s ease-in-out",
                        cursor: "pointer",
                        "&:hover": { borderColor: T.primary, backgroundColor: T.spineSoft }
                    }}
                >
                    <UploadCloud size={20} color={T.primary} style={{ flexShrink: 0 }} />
                    <MuiBox sx={{ flex: 1, minWidth: 0 }}>
                        <MuiTypography sx={{ fontSize: 13, fontWeight: 600, color: T.fg1 }}>Drop files here or click to browse</MuiTypography>
                        <MuiTypography sx={{ fontSize: 11.5, color: T.fg4 }}>PDF, documents, spreadsheets, markdown, code · max 50MB</MuiTypography>
                    </MuiBox>

                    {/* Hidden input for dropzone (drag&drop) */}
                    <input {...getInputProps()} />

                    {/* Dedicated hidden input for the button-driven selection — keep the existing accept attribute verbatim */}
                    <input ref={manualInputRef} type="file" multiple accept=".pdf,.doc,.docx,.odt,.rtf,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.txt,.html,.htm,.css,.csv,.tsv,.js,.mjs,.ts,.tsx,.jsx,.py,.java,.c,.h,.cs,.php,.rb,.pl,.sh,.bash,.xml,.yaml,.yml,.md,.markdown,.sql,.json,.graphql,.gql,.xhtml,.vue,.svelte,.ini,.cfg,.conf,.env,.toml,.properties,.log,.rst,.tex" style={{ display: "none" }} onChange={handleManualInputChange} />

                    <MuiButton
                        variant="outlined"
                        size="small"
                        onClick={handleBrowseClick}
                        sx={{ flexShrink: 0, textTransform: "none", borderColor: "rgba(0,0,0,0.12)", color: T.fg2 }}
                    >
                        Choose Files
                    </MuiButton>
                </MuiBox>

                {/* File listing */}
                <MuiBox sx={{ flex: 1, px: 3, pb: 3, pt: "18px", overflow: "hidden", minHeight: 0 }}>
                    <FileViewer ref={fileViewerRef} />
                </MuiBox>
            </MuiBox>

            {/* Upload Modal */}
            <FileUploadVectorFileModal
                isOpen={fileOpen}
                file={fileToUpload || undefined}
                onClose={() => {
                    setFileOpen(false);
                    setFileToUpload(null);
                }}
                onUploadComplete={async (uploadedFileId, uploadedFileName) => {
                    if (typeof uploadedFileId !== "number") return;
                    returnNotifiedForBatchRef.current = false;
                    // Refresh the file list first
                    await fileViewerRef.current?.refresh?.();
                    // Use the completion payload directly instead of a delayed
                    // name-based lookup. This avoids stale-view races when
                    // multiple uploads complete close together.
                    const existingScope = await fetchFileScope(uploadedFileId);
                    const queuedFile: VectorFile = {
                        fileid: uploadedFileId,
                        filename: uploadedFileName || `File #${uploadedFileId}`,
                        userid: 0,
                        mime_type: "",
                        createdate: new Date(0),
                        lastupdated: new Date(0),
                        faq: "",
                        overview: "",
                        total_tokens: 0,
                        search_index_chungs: 0,
                        content: "",
                        library: false,
                        projects: [],
                        filesize: 0
                    };
                    setPendingScopeAssignments(prev => [...prev, { file: queuedFile, scope: existingScope }]);
                }}
            />

            {/* Scope Assignment Modal */}
            <FileScopeModal
                open={showScopeModal}
                file={uploadedFile}
                currentScope={uploadedFileScope} // Pass existing scope (null for new files, populated for re-uploads)
                onClose={() => {
                    setShowScopeModal(false);
                    setUploadedFile(null);
                    setUploadedFileScope(null);
                    fileViewerRef.current?.refresh?.(); // Refresh to show updated scopes
                }}
                onSave={async (savedScope: SavedScopeData) => {
                    console.log("File scope saved:", savedScope);

                    const token = await getToken();
                    if (token) {
                        // Union of added+removed ids — anything that gained OR lost this file
                        // needs a rebuild check (removed_feature_ids carries cleared legacy
                        // feature scopes now that saves are product-only).
                        const allProjectIds = [
                            ...savedScope.changes.added_project_ids,
                            ...savedScope.changes.removed_project_ids
                        ];
                        const allFeatureIds = [
                            ...savedScope.changes.added_feature_ids,
                            ...savedScope.changes.removed_feature_ids
                        ];

                        if (savedScope.is_global) {
                            console.log("File saved as global scope, triggering global rebuild check");
                            markWorkProductsNeedingRebuild(null, null, null, null, true, token).catch(err => {
                                console.error("Error triggering global rebuild:", err);
                            });
                        }

                        if (allProjectIds.length > 0 || allFeatureIds.length > 0) {
                            console.log("Triggering rebuild for changed projects:", allProjectIds, "and features:", allFeatureIds);
                            markWorkProductsNeedingRebuild(
                                allProjectIds.length > 0 ? allProjectIds : null,
                                allFeatureIds.length > 0 ? allFeatureIds : null,
                                null, // user_story_ids
                                null, // source_warehouseid
                                true, // file_update
                                token
                            ).catch(err => {
                                console.error("Error triggering rebuild:", err);
                            });
                        }
                    }

                    // Reset state
                    setUploadedFileScope(null);

                    fileViewerRef.current?.refresh?.(); // Refresh to show updated scopes

                    // Assignment finished — let the host offer a ride back to
                    // wherever the user was before the agent brought them here,
                    // but only after the whole batch drains (effect above).
                    setCompletedAssignments(prev => prev + 1);
                }}
            />

            {/* File Type Error Dialog */}
            <Dialog open={!!fileTypeError} onOpenChange={(open) => !open && setFileTypeError(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>File Type Not Supported</DialogTitle>
                        <DialogDescription className="whitespace-pre-line">{fileTypeError}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={() => setFileTypeError(null)}>OK</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </MuiBox>
    );
};

export default UploadCenter;
